import * as React from 'react';
import { AvailabilitySearchQuery, LockOptions } from 'types';
import { Button } from 'omakase';
import { DisplayDate } from '@tock/time';
import { ExperienceCardAction } from 'utils/searchBar';
import { FC } from 'react';
import { LocalDate, LocalDateTime } from 'js-joda';
import { SearchTicketGroupWithSeatingAreas } from 'components/www/availability-search/AvailabilitySearchResultSimple';
import { consumer, core, safeToNumber } from '@tock/coretypes';
import { defaultMemoize } from 'reselect';
import { getClosestTicketGroups } from 'utils/citySearchUtils';
import {
  isOfferingEventType,
  isOnlineOrdersType,
  shouldRenderCTAFromActionAndQuery,
} from 'utils/offerings';
import OfferingDetailsSimple from 'components/business/offering/OfferingDetailsSimple';
import TimeAndSizeItemSimple from 'components/www/availability-search/TimeAndSizeItemSimple';

interface ExperienceResultSimpleProps {
  business: consumer.OfferingBusiness;
  lockedDateTime?: string;
  lockedSeatingArea?: core.SeatingArea;
  locale: string;
  offering: consumer.Offering;
  onLockTimeAndSize(
    ticketTypeId: number,
    selectedSize: number,
    dateTime: LocalDateTime,
    priceCents: number,
    options: LockOptions,
    ticketTypeName?: string
  ): void;
  /**
   * for date ranges we specify the first available date
   * @param business
   * @param experience
   * @param date
   */
  onNavigateToExperience(
    business: consumer.OfferingBusiness,
    experience: consumer.Offering,
    date?: string
  ): void;
  onNavigateToExperienceMenu(
    business: consumer.OfferingBusiness,
    experience: consumer.Offering
  ): void;
  query: AvailabilitySearchQuery;
  ticketGroup: SearchTicketGroupWithSeatingAreas[];
  timeZone: string;
  experienceCardAction: ExperienceCardAction;
}

const RESULTS_LIMIT = 4;

export const ExperienceResultSimple: FC<ExperienceResultSimpleProps> = ({
  business,
  lockedDateTime,
  lockedSeatingArea,
  locale,
  offering,
  onLockTimeAndSize,
  onNavigateToExperience,
  onNavigateToExperienceMenu,
  query,
  ticketGroup,
  timeZone,
  experienceCardAction,
}) => {
  const generateAvailabilities = defaultMemoize(
    (ticketGroups: SearchTicketGroupWithSeatingAreas[]) => {
      const { date, time } = query;
      return getClosestTicketGroups(ticketGroups, {
        timeSearched: time,
        dateSearched: date,
        limit: RESULTS_LIMIT,
      });
    }
  );

  const handleSelectTimeAndSize = (
    dateTime: LocalDateTime,
    priceCents: number,
    partySize: number,
    lockOptions?: Partial<LockOptions>
  ) => {
    const ticketTypeId = offering ? offering.id : -1; // TODO handle error

    const options = {
      ...(lockOptions || {}),
      showError: false,
    };

    onLockTimeAndSize(ticketTypeId, partySize, dateTime, priceCents, options, offering.name);
  };

  const handleNavigateToExperience = () => {
    const { dateStart } = getDateRange();
    if (onNavigateToExperience) {
      onNavigateToExperience(business, offering, query.dateEnd ? dateStart.toString() : undefined);
    }
  };

  const handleNavigateToExperienceMenu = (e: React.MouseEvent) => {
    e.preventDefault();

    if (onNavigateToExperienceMenu) {
      onNavigateToExperienceMenu(business, offering);
    }
  };

  const renderClosestAvailableTicketGroups = (
    closestAvailableTicketGroups: SearchTicketGroupWithSeatingAreas[]
  ): React.ReactNode => {
    const { size } = query;

    // If party size isn't set, a "Book for XX date" button should be displayed,
    // instead of available ticket groups.
    const partySize = size;
    if (!partySize) {
      return undefined;
    }

    return closestAvailableTicketGroups.map(
      (availableTicketGroup: SearchTicketGroupWithSeatingAreas, idx: number) => {
        return (
          <TimeAndSizeItemSimple
            key={`${availableTicketGroup.availableDateTime}-${availableTicketGroup.price}`}
            locale={locale}
            lockedDateTime={lockedDateTime}
            lockedSeatingArea={lockedSeatingArea}
            onClick={(dateTime, priceCents, partySize, lockOptions) =>
              handleSelectTimeAndSize(dateTime, priceCents, partySize, {
                exclusivity: availableTicketGroup.exclusivity,
                ...lockOptions,
              })
            }
            partySize={partySize}
            price={safeToNumber(availableTicketGroup.price)}
            priceDisplayStyle={offering.price?.priceDisplayStyle ?? 'BASE_TICKET_PRICE'}
            minRequiredSpend={{
              totalCents: safeToNumber(availableTicketGroup.minRequiredSpend?.totalCents),
              priceDisplayDistribution:
                availableTicketGroup.minRequiredSpend?.distribution ?? undefined,
            }}
            time={availableTicketGroup.availableDateTime}
            timeZone={timeZone}
            offering={offering}
            business={business}
            seatingAreas={availableTicketGroup.seatingAreas}
          />
        );
      }
    );
  };

  const getCallToActionType = (
    closestAvailableTicketGroups: consumer.SearchTicketGroup[]
  ): 'CTA' | 'TIMES' => {
    const type =
      isOfferingEventType(offering) && closestAvailableTicketGroups.length > 0
        ? 'CTA'
        : isOnlineOrdersType(offering)
        ? 'CTA'
        : shouldRenderCTAFromActionAndQuery(experienceCardAction, query)
        ? 'CTA'
        : 'TIMES';
    return type;
  };

  const sortedAvailableDateTimes = defaultMemoize((ticketGroup: consumer.SearchTicketGroup[]) => {
    const dateTimes = ticketGroup.map((group) => group.availableDateTime);
    dateTimes.sort();
    return dateTimes;
  });

  const getDateRange = () => {
    const dateTimes = sortedAvailableDateTimes(ticketGroup);

    const dateStart = LocalDate.from(LocalDateTime.parse(dateTimes[0]));
    const dateEnd = LocalDate.from(LocalDateTime.parse(dateTimes[dateTimes.length - 1]));
    return {
      dateStart,
      dateEnd,
    };
  };

  const renderTicketGroupDateRange = () => {
    const { dateStart, dateEnd } = getDateRange();
    return (
      <>
        <DisplayDate
          dateValue={dateStart}
          displayMonthAs="short"
          showWeekday={false}
          showYear={false}
        />
        {!dateStart.isEqual(dateEnd) && (
          <>
            -
            <DisplayDate
              dateValue={dateEnd}
              showMonth={dateStart.monthValue() !== dateEnd.monthValue()}
              displayMonthAs="short"
              showWeekday={false}
              showYear={false}
            />
          </>
        )}
      </>
    );
  };

  const renderBookNow = () => {
    if (query.dateEnd || !query.size) {
      return <>Book for {renderTicketGroupDateRange()}</>;
    }
    return 'Book now';
  };

  const renderOrderNow = () => {
    if (query.dateEnd || !query.size) {
      return <>Order for {renderTicketGroupDateRange()}</>;
    }
    return 'Order now';
  };

  const renderCallToAction = (
    closestAvailableTicketGroups: SearchTicketGroupWithSeatingAreas[]
  ) => {
    return isOfferingEventType(offering) && closestAvailableTicketGroups.length > 0 ? (
      <Button
        size="small"
        variant="primary"
        aria-label={`Get tickets for ${offering.name} (opens a dialog)`}
        onClick={handleNavigateToExperience}
      >
        Get tickets
      </Button>
    ) : isOnlineOrdersType(offering) ? (
      <>
        <Button
          variant="primary"
          size="small"
          aria-label={`Order now (opens a dialog)`}
          onClick={handleNavigateToExperience}
        >
          {renderOrderNow()}
        </Button>
        {offering.showMenuLink && (
          <Button
            className="Consumer-menuLink"
            aria-label={`View menu for ${offering.name} (opens a dialog)`}
            size="small"
            variant="basic"
            onClick={handleNavigateToExperienceMenu}
            data-testid="view-menu-button"
          >
            View menu
          </Button>
        )}
      </>
    ) : shouldRenderCTAFromActionAndQuery(experienceCardAction, query) ? (
      <Button
        variant="primary"
        size="small"
        aria-label={`Book now (opens a dialog)`}
        onClick={handleNavigateToExperience}
      >
        {renderBookNow()}
      </Button>
    ) : null;
  };

  const renderCTA = (
    ctaType: 'CTA' | 'TIMES',
    closestAvailableTicketGroups: SearchTicketGroupWithSeatingAreas[]
  ) => {
    if (ctaType === 'CTA') {
      return renderCallToAction(closestAvailableTicketGroups);
    }
    if (ctaType === 'TIMES') {
      return renderClosestAvailableTicketGroups(closestAvailableTicketGroups);
    }
    return null;
  };

  const closestAvailableTicketGroups = generateAvailabilities(
    ticketGroup
  ) as SearchTicketGroupWithSeatingAreas[];
  const ctaType = getCallToActionType(closestAvailableTicketGroups);

  return (
    <OfferingDetailsSimple
      business={business}
      offering={offering}
      onNavigateToExperience={handleNavigateToExperience}
      cta={renderCTA(ctaType, closestAvailableTicketGroups)}
      ctaType={ctaType}
    />
  );
};
